import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  Stack,
  TextField,
  MenuItem,
  Button,
  Chip,
  Paper,
  Autocomplete,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Collapse,
  useMediaQuery,
  Menu,
  Checkbox,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import MicIcon from '@mui/icons-material/Mic';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CodeIcon from '@mui/icons-material/Code';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import SearchOffRoundedIcon from '@mui/icons-material/SearchOffRounded';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  GridFooterContainer,
  GridPagination,
  useGridApiContext,
} from '@mui/x-data-grid';
import { auth, db, functions as firebaseFunctions } from '../firebase';
import { collection, query, where, getDocs, limit, orderBy, startAfter, getDoc, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import * as amplitude from '@amplitude/analytics-browser';

const ITEMS_PER_PAGE = 50;
const FETCH_BATCH_SIZE = 50;
const STORAGE_KEY = 'historyTablePrefs_v3';
const TOOLTIP_STYLES = {
  tooltip: {
    sx: {
      fontSize: '0.875rem',
    },
  },
};

const sourceLabel = (s) => {
  if (s === 'upload') return 'Վերբեռնում';
  if (s === 'recording') return 'Ձայնագրիչ';
  if (s === 'api') return 'API';
  return 'Այլ';
};

const statusLabel = (s) => {
  if (s === 'transcribed') return 'Վերծանված';
  if (s === 'processing') return 'Ընթացքում';
  if (s === 'uploading') return 'Վերբեռնվում է';
  if (s === 'converting') return 'Փոխակերպվում է';
  return 'Խափանված';
};

const statusIcon = (s) => {
  if (s === 'transcribed') return <CheckCircleOutlineIcon />;
  if (s === 'processing') return <AutorenewIcon />;
  if (s === 'uploading') return <CloudUploadIcon />;
  if (s === 'converting') return <AutorenewIcon />;
  return <ErrorOutlineIcon />;
};

const statusColor = (s) => {
  if (s === 'transcribed') return 'success.main';
  if (s === 'processing') return 'warning.main';
  if (s === 'uploading') return 'warning.main';
  if (s === 'converting') return 'warning.main';
  return 'error.main';
};

const sourceIcon = (s) => {
  if (s === 'upload') return <CloudUploadIcon />;
  if (s === 'recording') return <MicIcon />;
  if (s === 'api') return <CodeIcon />;
  return <CloudUploadIcon />;
};

const parseHms = (str) => {
  const parts = String(str).trim().split(':').map(Number);
  if (parts.some((n) => Number.isNaN(n))) return null;
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 1) return parts[0];
  return null;
};

const toSeconds = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return Math.max(0, Math.floor(v));
  if (typeof v === 'string') {
    const hms = parseHms(v);
    if (hms != null) return Math.max(0, Math.floor(hms));
    const n = Number(v);
    if (!Number.isNaN(n)) return Math.max(0, Math.floor(n));
    return 0;
  }
  return 0;
};

const toMs = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toDate === 'function') {
    const d = ts.toDate();
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (ts instanceof Date) return Number.isNaN(ts.getTime()) ? 0 : ts.getTime();
  if (typeof ts === 'number') {
    if (ts > 1e12) return ts;
    if (ts > 1e9) return ts * 1000;
    return ts;
  }
  if (typeof ts === 'string') {
    const num = Number(ts);
    if (!Number.isNaN(num)) {
      if (num > 1e12) return num;
      if (num > 1e9) return num * 1000;
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? 0 : d.getTime();
  }
  if (typeof ts === 'object' && typeof ts.seconds === 'number') {
    return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6);
  }
  return 0;
};

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.floor(Number(seconds || 0)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h ? `${h} ժ ` : ''}${m ? `${m} ր ` : ''}${sec} վրկ`;
};

const formatMs = (ms) => {
  if (!ms) return '';
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const time = d.toLocaleTimeString('hy-AM', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day}.${month}.${year} ${time}`;
};

const newFilter = () => ({
  id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  key: '',
  values: [],
});

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const addDays = (d, n) => new Date(d.getTime() + n * 86400000);
const startOfWeekMon = (d) => {
  const day = d.getDay() || 7;
  return startOfDay(addDays(d, 1 - day));
};
const endOfWeekSun = (d) => endOfDay(addDays(startOfWeekMon(d), 6));
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0));

const getRangeFromPreset = (preset) => {
  const now = new Date();
  const todayStart = startOfDay(now);
  const todayEnd = endOfDay(now);

  if (preset === 'today') return { from: todayStart, to: todayEnd };
  if (preset === 'yesterday') {
    const y = addDays(todayStart, -1);
    return { from: y, to: endOfDay(y) };
  }
  if (preset === 'last7') return { from: addDays(todayStart, -6), to: todayEnd };
  if (preset === 'thisWeek') return { from: startOfWeekMon(now), to: todayEnd };
  if (preset === 'lastWeek') {
    const endPrev = endOfWeekSun(addDays(now, -7));
    return { from: addDays(endPrev, -6), to: endPrev };
  }
  if (preset === 'thisMonth') return { from: startOfMonth(now), to: todayEnd };
  if (preset === 'lastMonth') {
    const lastMonthDate = addDays(startOfMonth(now), -1);
    return { from: startOfMonth(lastMonthDate), to: endOfMonth(lastMonthDate) };
  }
  return { from: null, to: null };
};

const History = () => {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastCursorId, setLastCursorId] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  const [sourceFilter, setSourceFilter] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [starredFilter, setStarredFilter] = useState(false);
  const [filters, setFilters] = useState([]);
  const [sortModel, setSortModel] = useState([{ field: 'createdAtMs', sort: 'desc' }]);
  const [paginationModel, setPaginationModel] = useState({ pageSize: ITEMS_PER_PAGE, page: 0 });

  const [userPlan, setUserPlan] = useState('');
  const [hydrated, setHydrated] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState('');

  const navigate = useNavigate();
  const firstLoadRef = useRef(false);
  const loadingRef = useRef(false);

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
  const listRecordingsForHistory = useMemo(
    () => httpsCallable(firebaseFunctions, 'listRecordingsForHistory'),
    []
  );
  const isPageBusy = loading;

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
      if (saved.sourceFilter) setSourceFilter(saved.sourceFilter);
      if (typeof saved.datePreset === 'string') setDatePreset(saved.datePreset);
      if (typeof saved.dateFrom === 'string') setDateFrom(saved.dateFrom);
      if (typeof saved.dateTo === 'string') setDateTo(saved.dateTo);
      if (typeof saved.statusFilter === 'string') setStatusFilter(saved.statusFilter);
      if (typeof saved.starredFilter === 'boolean') setStarredFilter(saved.starredFilter);
      if (Array.isArray(saved.filters)) setFilters(saved.filters);
      if (Array.isArray(saved.sortModel) && saved.sortModel.length) setSortModel(saved.sortModel);
      if (saved.pageSize && Number(saved.pageSize) > 0) {
        setPaginationModel((p) => ({ ...p, pageSize: Number(saved.pageSize) }));
      }
    } catch { }
    setHydrated(true);
  }, []);

  useEffect(() => {
    if (!hydrated) return;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        sourceFilter,
        datePreset,
        dateFrom,
        dateTo,
        statusFilter,
        starredFilter,
        filters,
        sortModel,
        pageSize: paginationModel.pageSize,
      })
    );
  }, [
    sourceFilter,
    datePreset,
    dateFrom,
    dateTo,
    statusFilter,
    starredFilter,
    filters,
    sortModel,
    paginationModel.pageSize,
    hydrated,
  ]);

  useEffect(() => {
    const fetchPlan = async () => {
      const user = auth.currentUser;
      if (!user) return;
      try {
        const snap = await getDoc(doc(db, 'users', user.uid));
        const plan = (snap.exists() && (snap.data().plan || '')) || '';
        setUserPlan(String(plan || ''));
      } catch { }
    };
    fetchPlan();
  }, []);

  const isBusiness = useMemo(() => {
    const p = String(userPlan || '').toLowerCase();
    return p.includes('business');
  }, [userPlan]);

  useEffect(() => {
    setFiltersOpen(!isXs);
    const nextVisibilityModel = isXs
      ? { starred: false, duration: false, source: false, status: false, title: true, createdAtMs: true }
      : { starred: true, duration: true, source: true, status: true, title: true, createdAtMs: true };
    setColumnVisibilityModel(nextVisibilityModel);
  }, [isXs]);

  const fetchPageFallback = useCallback(async (cursorId = null) => {
    const user = auth.currentUser;
    if (!user) return { items: [], nextCursorId: null, hasMore: false };

    const recordingsRef = collection(db, 'recordings');
    let fallbackQuery = query(
      recordingsRef,
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(FETCH_BATCH_SIZE)
    );

    if (cursorId) {
      const cursorSnap = await getDoc(doc(db, 'recordings', cursorId));
      if (cursorSnap.exists() && cursorSnap.data()?.userId === user.uid) {
        fallbackQuery = query(
          recordingsRef,
          where('userId', '==', user.uid),
          orderBy('createdAt', 'desc'),
          startAfter(cursorSnap),
          limit(FETCH_BATCH_SIZE)
        );
      }
    }

    const snap = await getDocs(fallbackQuery);
    const items = snap.docs
      .map((docSnap) => {
        const row = docSnap.data() || {};
        return {
          id: docSnap.id,
          title: row.title || '',
          duration: row.duration || 0,
          source: row.source || 'upload',
          createdAtMs: toMs(row.createdAt),
          status: row.status || '',
          starred: !!row.starred,
          metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : null,
        };
      })
      .filter((item) => item.source !== 'agent');

    return {
      items,
      nextCursorId: snap.docs.length > 0 ? snap.docs[snap.docs.length - 1].id : null,
      hasMore: snap.docs.length === FETCH_BATCH_SIZE,
    };
  }, []);

  const fetchPage = useCallback(async (cursorId = null, fromLoadMore = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    try {
      let payload = null;
      try {
        const response = await listRecordingsForHistory({
          pageSize: FETCH_BATCH_SIZE,
          cursorId: cursorId || null,
        });
        payload = response?.data || {};
      } catch (callableError) {
        console.warn('History callable unavailable, using direct Firestore fallback.', callableError);
        payload = await fetchPageFallback(cursorId);
        amplitude.track('History Fetch Fallback Used', {
          error: callableError?.message || String(callableError),
        });
      }

      const batch = Array.isArray(payload.items) ? payload.items : [];

      let appendedCount = 0;
      setRecordings((prev) => {
        const existing = new Set(prev.map((r) => r.id));
        const toAdd = batch.filter((item) => !existing.has(item.id));
        appendedCount = toAdd.length;
        return [...prev, ...toAdd];
      });

      setLastCursorId(typeof payload.nextCursorId === 'string' ? payload.nextCursorId : null);
      setHasMore(Boolean(payload.hasMore));
      amplitude.track('Recordings Fetched', { count: batch.length });

      if (fromLoadMore && appendedCount > 0) {
        setSnackbarMsg(`Բեռնվեց ևս ${appendedCount} ձայնագրություն`);
        setSnackbarOpen(true);
        amplitude.track('Recordings Load More Success', { added: appendedCount });
      }
    } catch (e) {
      console.error('Fetch Page Error: ', e);
      amplitude.track('Fetch Recordings Failed', { error: e?.message || String(e) });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [listRecordingsForHistory, fetchPageFallback]);

  useEffect(() => {
    if (firstLoadRef.current) return;
    firstLoadRef.current = true;
    fetchPage(null);
  }, [fetchPage]);


  const normalizeToStrings = (value) => {
    if (Array.isArray(value)) return value.map((v) => String(v));
    if (value == null) return [];
    if (typeof value === 'object') return [JSON.stringify(value)];
    return [String(value)];
  };

  const availableKeys = useMemo(() => {
    if (!isBusiness) return [];
    const set = new Set();
    recordings.forEach((r) => {
      const m = r && r.metadata ? r.metadata : null;
      if (m && typeof m === 'object') {
        Object.keys(m).forEach((k) => set.add(k));
      }
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'hy-AM'));
  }, [recordings, isBusiness]);

  const valuesByKey = useMemo(() => {
    if (!isBusiness || filters.length === 0) return {};
    const map = new Map();
    recordings.forEach((r) => {
      const m = r && r.metadata ? r.metadata : null;
      if (!m) return;
      Object.entries(m).forEach(([k, v]) => {
        const arr = normalizeToStrings(v);
        let set = map.get(k);
        if (!set) {
          set = new Set();
          map.set(k, set);
        }
        arr.forEach((s) => set.add(s));
      });
    });
    const out = {};
    map.forEach((set, k) => {
      out[k] = Array.from(set).sort((a, b) => a.localeCompare(b, 'hy-AM'));
    });
    return out;
  }, [recordings, isBusiness, filters.length]);

  const preparedBusinessFilters = useMemo(() => {
    if (!isBusiness || filters.length === 0) return [];
    return filters.map((f) => {
      const key = (f.key || '').trim();
      const selected = Array.isArray(f.values) ? f.values.map(String) : [];
      return { key, selectedSet: new Set(selected) };
    });
  }, [filters, isBusiness]);

  const isDefaultState = useMemo(() => {
    const hasDate = datePreset !== 'all' && !(datePreset === 'custom' && (!dateFrom || !dateTo));
    return (
      sourceFilter === 'all' && !hasDate && statusFilter === 'all' && filters.length === 0 && !starredFilter
    );
  }, [sourceFilter, datePreset, dateFrom, dateTo, statusFilter, filters, starredFilter]);

  const filteredRows = useMemo(() => {
    let fromMs = 0;
    let toMsLimit = 0;

    if (datePreset === 'custom') {
      if (dateFrom) {
        const start = new Date(dateFrom);
        fromMs = Number.isNaN(start.getTime()) ? 0 : start.getTime();
      }
      if (dateTo) {
        const t = new Date(dateTo);
        const end = new Date(t.getFullYear(), t.getMonth(), t.getDate(), 23, 59, 59, 999);
        toMsLimit = Number.isNaN(end.getTime()) ? 0 : end.getTime();
      }
    } else {
      const r = getRangeFromPreset(datePreset);
      fromMs = r.from ? r.from.getTime() : 0;
      toMsLimit = r.to ? r.to.getTime() : 0;
    }

    return recordings
      .filter((r) => {
        if (sourceFilter !== 'all' && (r.source || 'upload') !== sourceFilter) return false;

        if (fromMs || toMsLimit) {
          const createdAtMs = typeof r.createdAtMs === 'number' ? r.createdAtMs : toMs(r.createdAt);
          if (!createdAtMs) return false;
          if (fromMs && createdAtMs < fromMs) return false;
          if (toMsLimit && createdAtMs > toMsLimit) return false;
        }

        if (statusFilter !== 'all') {
          if ((r.status || '').toLowerCase() !== statusFilter) return false;
        }

        if (starredFilter && !r.starred) return false;

        if (preparedBusinessFilters.length > 0) {
          const m = r && r.metadata ? r.metadata : null;
          if (!m) return false;
          for (const f of preparedBusinessFilters) {
            if (!f.key || f.selectedSet.size === 0) return false;
            const valuesAsStrings = normalizeToStrings(m[f.key]);
            if (valuesAsStrings.length === 0) return false;
            const match = valuesAsStrings.some((s) => f.selectedSet.has(String(s)));
            if (!match) return false;
          }
        }

        return true;
      })
      .map((r) => {
        const createdAtMs = typeof r.createdAtMs === 'number' ? r.createdAtMs : toMs(r.createdAt);
        const durationSec = toSeconds(r.duration);

        const baseData = {
          id: r.id,
          title: r.title || 'Անվերնագիր ձայնագրություն',
          duration: durationSec,
          source: r.source || 'upload',
          createdAtMs,
          createdAtText: formatMs(createdAtMs),
          status: r.status,
          starred: r.starred || false,
        };
        return baseData;
      });
  }, [
    recordings,
    sourceFilter,
    datePreset,
    dateFrom,
    dateTo,
    statusFilter,
    starredFilter,
    preparedBusinessFilters,
  ]);

  const handleToggleStar = useCallback(async (recordingId, currentStarred) => {
    const newStarred = !currentStarred;
    try {
      // Update in Firebase
      const recordingRef = doc(db, 'recordings', recordingId);
      await updateDoc(recordingRef, { starred: newStarred });

      // Update local state
      setRecordings((prev) =>
        prev.map((r) => r.id === recordingId ? { ...r, starred: newStarred } : r)
      );

      amplitude.track('Recording Starred From List', { docId: recordingId, starred: newStarred });
    } catch (error) {
      console.error('Error toggling star:', error);
      setSnackbarMsg('Տեսնել աստղիկները');
      setSnackbarOpen(true);
    }
  }, []);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'starred',
        headerName: '',
        description: 'Աստղ',
        width: isXs ? 48 : 60,
        align: 'center',
        headerAlign: 'center',
        sortable: true,
        renderCell: (params) => {
          const isStarred = params.value;
          return (
            <Tooltip title={isStarred ? "Հեռացնել աստղը" : "Ավելացնել աստղ"}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleToggleStar(params.row.id, isStarred);
                }}
                sx={{
                  color: isStarred ? 'warning.main' : 'text.disabled',
                  '&:hover': {
                    color: 'warning.main'
                  }
                }}
              >
                {isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
              </IconButton>
            </Tooltip>
          );
        },
      },
      {
        field: 'title',
        headerName: 'Վերնագիր',
        description: 'Վերնագիր',
        flex: isXs ? 1.4 : 1.7,
        minWidth: isXs ? 170 : 240,
        renderCell: (params) => {
          const row = params.row;
          return (
            <Stack
              spacing={0.35}
              sx={{
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                py: isXs ? 0.25 : 0,
                justifyContent: isXs ? 'flex-start' : 'center',
              }}
            >
              <Typography variant="body2" sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontWeight: 500 }}>
                {row.title}
              </Typography>
              {isXs && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                  <Tooltip title={`Աղբյուր: ${sourceLabel(row.source)}`} componentsProps={TOOLTIP_STYLES}>
                    <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'text.secondary', flexShrink: 0 }}>
                      {sourceIcon(row.source)}
                    </Box>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                    {formatDuration(row.duration || 0)}
                  </Typography>
                  <Tooltip title={`Կարգավիճակ: ${statusLabel(row.status)}`} componentsProps={TOOLTIP_STYLES}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: statusColor(row.status),
                        flexShrink: 0,
                        '& > svg': { fontSize: 17 },
                      }}
                    >
                      {statusIcon(row.status)}
                    </Box>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          );
        },
        sortable: true,
      },
      {
        field: 'duration',
        headerName: 'Տևողություն',
        description: 'Տևողություն',
        width: 140,
        renderCell: (params) => <Box>{formatDuration(params.row.duration || 0)}</Box>,
        sortComparator: (v1, v2) => (v1 || 0) - (v2 || 0),
      },
      {
        field: 'source',
        headerName: 'Աղբյուր',
        description: 'Աղբյուր',
        width: 130,
        renderCell: (params) => (
          <Chip size="small" icon={sourceIcon(params.value)} label={sourceLabel(params.value)} variant="outlined" />
        ),
        sortable: true,
      },
      {
        field: 'createdAtMs',
        headerName: 'Ամսաթիվ',
        description: 'Ամսաթիվ',
        width: isXs ? 150 : 190,
        renderCell: (params) => (
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {params.row.createdAtText}
          </Box>
        ),
        sortComparator: (a, b) => (a || 0) - (b || 0),
      },
      {
        field: 'status',
        headerName: 'Կարգավիճակ',
        description: 'Կարգավիճակ',
        width: 140,
        renderCell: (params) => (
          <Chip
            label={
              params.value === 'transcribed'
                ? 'Վերծանված'
                : params.value === 'processing'
                  ? 'Ընթացքում'
                  : params.value === 'uploading'
                    ? 'Վերբեռնվում է'
                    : params.value === 'converting'
                      ? 'Փոխակերպվում է'
                      : 'Խափանված'
            }
            size="small"
            variant="outlined"
            color={
              params.value === 'processing' || params.value === 'uploading' || params.value === 'converting'
                ? 'warning'
                : params.value === 'failed'
                  ? 'error'
                  : 'default'
            }
            sx={{ fontWeight: 500 }}
          />
        ),
        sortable: true,
      },
    ];

    return baseColumns;
  }, [handleToggleStar, isXs]);

  const resetFiltersAndPreferences = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setSourceFilter('all');
    setDatePreset('all');
    setDateFrom('');
    setDateTo('');
    setStatusFilter('all');
    setStarredFilter(false);
    setFilters([]);
    setSortModel([{ field: 'createdAtMs', sort: 'desc' }]);
    setPaginationModel((p) => ({ ...p, page: 0, pageSize: ITEMS_PER_PAGE }));
    if (isXs) {
      setColumnVisibilityModel({
        duration: false,
        source: false,
        status: false,
        title: true,
        createdAtMs: true,
      });
    } else {
      setColumnVisibilityModel({
        duration: true,
        source: true,
        status: true,
        title: true,
        createdAtMs: true,
      });
    }
  }, [isXs]);

  const handleAddFilter = () => {
    setFilters((prev) => [...prev, newFilter()]);
    amplitude.track('Metadata Filter Added');
  };

  const handleRemoveFilter = (id) => {
    setFilters((prev) => prev.filter((f) => f.id !== id));
    amplitude.track('Metadata Filter Removed');
  };

  const CustomToolbar = () => {
    const api = useGridApiContext();
    const [moreMenuAnchorEl, setMoreMenuAnchorEl] = React.useState(null);
    const isMoreMenuOpen = Boolean(moreMenuAnchorEl);

    const handleMoreMenuOpen = (event) => {
      setMoreMenuAnchorEl(event.currentTarget);
    };

    const handleMoreMenuClose = () => {
      setMoreMenuAnchorEl(null);
    };

    return (
      <GridToolbarContainer
        sx={{
          p: 1,
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
          backgroundColor: (t) => (t.palette.mode === 'light' ? '#fafafa' : t.palette.background.paper),
        }}
      >
        {isXs && (
          <Stack direction="row" alignItems="center" sx={{ width: '100%' }}>
            <Button
              startIcon={<FilterListIcon />}
              variant="outlined"
              size="small"
              onClick={() => setFiltersOpen((v) => !v)}
              sx={{ width: '100%' }}
            >
              {filtersOpen ? 'Թաքցնել ֆիլտրերը' : 'Ցուցադրել ֆիլտրերը'}
            </Button>
          </Stack>
        )}

        <Collapse in={filtersOpen} unmountOnExit sx={{ width: '100%' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ width: '100%', flexWrap: 'wrap', mt: 1 }}
          >
            <Tooltip title="Տեսնել աստղիկները" componentsProps={TOOLTIP_STYLES}>
              <Checkbox
                checked={starredFilter}
                onChange={(e) => setStarredFilter(e.target.checked)}
                size="small"
                icon={<StarIcon sx={{ color: 'text.secondary' }} />}
                checkedIcon={<StarIcon sx={{ color: 'warning.main' }} />}
                sx={{
                  minWidth: { xs: 'auto', sm: 'auto' },
                  mr: { xs: 0, sm: 1 }
                }}
              />
            </Tooltip>

            <GridToolbarQuickFilter
              placeholder="Որոնել վերնագրով…"
              quickFilterParser={(v) => v.split(/\s+/).filter(Boolean)}
              sx={{ flex: 1, minWidth: { xs: '100%', sm: 260 } }}
            />



            <TextField
              size="small"
              label="Ժամանակահատված"
              select
              value={datePreset}
              onChange={(e) => setDatePreset(e.target.value)}
              fullWidth={isXs}
              sx={{ minWidth: { xs: '100%', sm: 190 } }}
            >
              <MenuItem value="all">Բոլորը</MenuItem>
              <MenuItem value="today">Այսօր</MenuItem>
              <MenuItem value="yesterday">Երեկ</MenuItem>
              <MenuItem value="last7">Վերջին 7 օրը</MenuItem>
              <MenuItem value="thisWeek">Շաբաթը (մինչ այժմ)</MenuItem>
              <MenuItem value="lastWeek">Անցած շաբաթ</MenuItem>
              <MenuItem value="thisMonth">Ամիսը (մինչ այժմ)</MenuItem>
              <MenuItem value="lastMonth">Անցած ամիս</MenuItem>
              <MenuItem value="custom">Ընտրովի միջակայք</MenuItem>
            </TextField>

            {datePreset === 'custom' && (
              <>
                <TextField
                  size="small"
                  label="Սկիզբ"
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth={isXs}
                />
                <TextField
                  size="small"
                  label="Ավարտ"
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  fullWidth={isXs}
                />
              </>
            )}

            <TextField
              size="small"
              label="Կարգավիճակ"
              select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              fullWidth={isXs}
              sx={{ minWidth: { xs: '100%', sm: 150 } }}
            >
              <MenuItem value="all">Բոլորը</MenuItem>
              <MenuItem value="uploading">Վերբեռնվում է</MenuItem>
              <MenuItem value="converting">Փոխակերպվում է</MenuItem>
              <MenuItem value="processing">Ընթացքում</MenuItem>
              <MenuItem value="transcribed">Վերծանված</MenuItem>
              <MenuItem value="failed">Խափանված</MenuItem>
            </TextField>

            {!isDefaultState && (
              <Button
                variant="outlined"
                size="small"
                onClick={resetFiltersAndPreferences}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Չֆիլտրել
              </Button>
            )}

            <Tooltip title="Այլ գործողություններ" componentsProps={TOOLTIP_STYLES}>
              <IconButton onClick={handleMoreMenuOpen} sx={{ ml: { xs: 0, sm: 1 } }}>
                <MoreVertIcon />
              </IconButton>
            </Tooltip>
            <Menu anchorEl={moreMenuAnchorEl} open={isMoreMenuOpen} onClose={handleMoreMenuClose}>
              <MenuItem
                onClick={() => {
                  api.current.showPreferences('columns');
                  handleMoreMenuClose();
                }}
              >
                Սյուների տեսանելիություն
              </MenuItem>
              {isBusiness && (
                <MenuItem
                  onClick={() => {
                    handleAddFilter();
                    handleMoreMenuClose();
                  }}
                >
                  Ավելացնել մետատվյալների ֆիլտր
                </MenuItem>
              )}
            </Menu>
          </Stack>

          {isBusiness && filters.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ width: '100%', mt: 1.5 }}>
              {filters.map((f) => {
                const keyExists = f.key && availableKeys.includes(f.key);
                const valueOptions = keyExists ? valuesByKey[f.key] || [] : [];
                return (
                  <Stack
                    key={f.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    sx={{
                      flex: '1 1 480px',
                      p: 1.5,
                      border: (theme) => `1px solid ${theme.palette.divider}`,
                      borderRadius: 2,
                    }}
                  >
                    <Autocomplete
                      options={availableKeys}
                      value={f.key || ''}
                      onChange={(e, val) => {
                        setFilters((prev) =>
                          prev.map((x) => (x.id === f.id ? { ...x, key: val || '', values: [] } : x))
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Մետատվ. բանալի"
                          placeholder="ընտրեք բանալի"
                          helperText={!availableKeys.length ? 'Բանալիներ դեռ չկան' : ''}
                          fullWidth
                        />
                      )}
                      sx={{ minWidth: 200, flex: 1 }}
                      disableClearable={false}
                      autoSelect
                      autoHighlight
                      openOnFocus
                    />
                    <Autocomplete
                      multiple
                      freeSolo
                      options={valueOptions}
                      value={f.values || []}
                      onChange={(e, newValue) => {
                        setFilters((prev) => prev.map((x) => (x.id === f.id ? { ...x, values: newValue } : x)));
                      }}
                      disabled={!keyExists}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label="Արժեք(ներ)"
                          placeholder={keyExists ? 'Ընտրեք կամ մուտքագրեք' : 'Նախ ընտրեք բանալի'}
                          fullWidth
                        />
                      )}
                      sx={{ minWidth: 220, flex: 2 }}
                    />
                    <Tooltip title="Ջնջել ֆիլտրը" componentsProps={TOOLTIP_STYLES}>
                      <IconButton onClick={() => handleRemoveFilter(f.id)} size="small">
                        <DeleteOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Collapse>
      </GridToolbarContainer>
    );
  };

  const EmptyStateCard = ({ icon, title, description, children, compact = false }) => (
    <Paper
      variant="outlined"
      sx={{
        width: '100%',
        maxWidth: compact ? 520 : 700,
        mx: 'auto',
        p: compact ? 2.5 : { xs: 2.5, sm: 4 },
        borderRadius: 3,
        borderColor: 'divider',
        background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.14 : 0.22)} 0%, ${alpha(theme.palette.info.main, theme.palette.mode === 'light' ? 0.04 : 0.08)} 100%)`,
      }}
    >
      <Stack alignItems="center" spacing={compact ? 1.25 : 1.75} textAlign="center">
        <Box
          sx={{
            width: compact ? 48 : 64,
            height: compact ? 48 : 64,
            borderRadius: '50%',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'primary.main',
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.12 : 0.24),
            border: `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.35 : 0.6)}`,
            '& svg': { fontSize: compact ? 24 : 30 },
          }}
        >
          {icon}
        </Box>
        <Typography variant={compact ? 'h6' : 'h5'} sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 560 }}>
          {description}
        </Typography>
        {children}
      </Stack>
    </Paper>
  );

  const CustomNoRowsOverlay = () => {
    const label = isDefaultState
      ? 'Այս պահին ցուցադրելու ձայնագրություններ չկան'
      : 'Ընտրված ֆիլտրերով ոչինչ չի գտնվել';
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: { xs: 1.5, sm: 2.5 } }}>
        <EmptyStateCard
          compact
          icon={<SearchOffRoundedIcon />}
          title="Արդյունքներ չկան"
          description={label}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1, width: { xs: '100%', sm: 'auto' } }}>
            {!isDefaultState && (
              <Button variant="outlined" onClick={resetFiltersAndPreferences} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Չֆիլտրել
              </Button>
            )}
            {hasMore && (
              <Button
                variant="contained"
                onClick={() => {
                  amplitude.track('Load More Click', { place: 'noRowsOverlay' });
                  fetchPage(lastCursorId, true);
                }}
                disabled={isPageBusy}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Բեռնել ավել տվյալներ
              </Button>
            )}
          </Stack>
        </EmptyStateCard>
      </Stack>
    );
  };

  const CustomNoResultsOverlay = () => {
    const label = hasMore
      ? 'Բեռնված տվյալների մեջ ոչինչ չգտնվեց'
      : 'Ոչինչ չգտնվեց';
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: { xs: 1.5, sm: 2.5 } }}>
        <EmptyStateCard
          compact
          icon={<SearchOffRoundedIcon />}
          title="Որոնման արդյունք չկա"
          description={label}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1, width: { xs: '100%', sm: 'auto' } }}>
            {!isDefaultState && (
              <Button variant="outlined" onClick={resetFiltersAndPreferences} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                Չֆիլտրել
              </Button>
            )}
            {hasMore && (
              <Button
                variant="contained"
                onClick={() => {
                  amplitude.track('Load More Click', { place: 'noResultsOverlay' });
                  fetchPage(lastCursorId, true);
                }}
                disabled={isPageBusy}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Բեռնել ավել տվյալներ
              </Button>
            )}
          </Stack>
        </EmptyStateCard>
      </Stack>
    );
  };

  const CustomFooter = () => {
    return (
      <GridFooterContainer sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <GridPagination />
        {hasMore && filteredRows.length > 0 && (
          <Button
            variant="contained"
            size="small"
            onClick={() => {
              amplitude.track('Load More Click', { place: 'footer' });
              fetchPage(lastCursorId, true);
            }}
            disabled={isPageBusy}
            sx={{ ml: 1 }}
          >
            Բեռնել ավել տվյալներ
          </Button>
        )}
      </GridFooterContainer>
    );
  };

  const hyLocaleText = useMemo(
    () => ({
      noRowsLabel: 'Տվյալներ չկան',
      toolbarQuickFilterPlaceholder: 'Որոնել…',
      MuiTablePagination: {
        labelRowsPerPage: 'Տողեր յուրաքանչյուր էջում:',
        labelDisplayedRows: ({ from, to, count }) =>
          `${from}–${to} / ${count !== -1 ? count : `ավելին քան ${to}`}`,
      },
    }),
    []
  );

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2 },
        width: '100%',
        minWidth: 0,
        height: { xs: 'auto', sm: 'calc(100vh - 64px)' },
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
        Պատմություն
      </Typography>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 1, sm: 1.5 },
          width: '100%',
          minWidth: 0,
          flex: isXs ? '0 0 auto' : 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {isPageBusy && recordings.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <LinearProgress style={{ width: '100%' }} />
          </Box>
        )}

        {recordings.length > 0 ? (
          <>
            <Box sx={{ flex: isXs ? '0 0 auto' : 1, width: '100%', minWidth: 0, minHeight: isXs ? 'auto' : 360 }}>
              <DataGrid
                rows={filteredRows}
                columns={columns}
                loading={isPageBusy}
                autoHeight={isXs}
                disableColumnMenu
                disableRowSelectionOnClick
                density={isXs ? 'compact' : 'standard'}
                rowHeight={isXs ? 72 : 60}
                columnHeaderHeight={64}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                slots={{
                  toolbar: CustomToolbar,
                  noRowsOverlay: CustomNoRowsOverlay,
                  noResultsOverlay: CustomNoResultsOverlay,
                  footer: CustomFooter,
                }}
                localeText={hyLocaleText}
                sx={{
                  height: '100%',
                  '& .MuiDataGrid-columnHeaders': { fontWeight: 700 },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    whiteSpace: 'normal',
                    lineHeight: 1.4,
                    textAlign: 'center',
                  },
                  '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
                  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                  '& .MuiDataGrid-row': { cursor: 'pointer' },
                }}
                onRowClick={(params) => {
                  navigate(`/recording-details/${encodeURIComponent(params.id)}`);
                  amplitude.track('Recording Viewed', {
                    id: params.id,
                    status: params.row.status,
                    duration: params.row.duration,
                  });
                }}
              />
            </Box>

            {isPageBusy && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <LinearProgress style={{ width: '100%' }} />
              </Box>
            )}
          </>
        ) : (
          !loading && (
            <Box sx={{ py: { xs: 2, sm: 3 } }}>
              <EmptyStateCard
                icon={<HistoryRoundedIcon />}
                title="Պատմությունը դեռ դատարկ է"
                description="Երբ վերբեռնեք կամ ձայնագրեք առաջին աուդիոն, այն կերևա այստեղ։ Այստեղից կարող եք հեշտությամբ որոնել, ֆիլտրել և բացել բոլոր վերծանումները։"
              >
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mt: 1, width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon />}
                    onClick={() => navigate('/new')}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Ստեղծել առաջին ձայնագրությունը
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={() => navigate('/new')}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    Վերբեռնել աուդիո
                  </Button>
                </Stack>
              </EmptyStateCard>
            </Box>
          )
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success" variant="filled" sx={{ width: '100%' }}>
          {snackbarMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default History;
